import React from "react";
import { Page, Card } from "../components/UI";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { Moon, Sun, Shield, Globe, Keyboard } from "lucide-react";
import { AvailabilitySettings } from "../components/AvailabilitySettings";

export default function Settings() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();

  return (
    <Page title="Settings" subtitle="Profile, theme, and preferences.">
      <div className="grid md:grid-cols-2 gap-5">
        <Card delay={0.05} testid="settings-profile">
          <h3 className="font-serif text-xl mb-4">Profile</h3>
          <div className="space-y-3 text-sm">
            <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--mf-text-muted)" }}>Name</div><div>{user?.full_name}</div></div>
            <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--mf-text-muted)" }}>Email</div><div>{user?.email}</div></div>
            <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--mf-text-muted)" }}>Role</div><div className="capitalize">{user?.role}</div></div>
            {user?.practice_name && <div><div className="text-xs uppercase tracking-wider" style={{ color: "var(--mf-text-muted)" }}>Practice</div><div>{user.practice_name}</div></div>}
          </div>
        </Card>

        <AvailabilitySettings />
        <Card delay={0.1} testid="settings-theme">
          <h3 className="font-serif text-xl mb-4">Appearance</h3>
          <button onClick={toggle} className="w-full flex items-center justify-between p-4 rounded-xl" style={{ background: "var(--mf-muted)" }} data-testid="settings-theme-toggle">
            <div className="flex items-center gap-3">
              {theme === "dark" ? <Sun className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> : <Moon className="w-5 h-5" style={{ color: "var(--mf-primary)" }} />}
              <span className="text-sm font-medium">{theme === "dark" ? "Light mode" : "Dark mode"}</span>
            </div>
            <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Click to toggle</span>
          </button>
        </Card>

        <Card delay={0.15} testid="settings-security">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5" style={{ color: "var(--mf-primary)" }} />
            <h3 className="font-serif text-xl">Security & HIPAA</h3>
          </div>
          <ul className="text-sm space-y-2" style={{ color: "var(--mf-text-muted)" }}>
            <li>• AES-256 encryption for data at rest</li>
            <li>• Auto-logout after 15 minutes idle</li>
            <li>• Audit logs on every record access</li>
            <li>• BAA available for Enterprise tier</li>
          </ul>
        </Card>

        <Card delay={0.2} testid="settings-shortcuts">
          <div className="flex items-center gap-2 mb-4">
            <Keyboard className="w-5 h-5" style={{ color: "var(--mf-primary)" }} />
            <h3 className="font-serif text-xl">Keyboard Shortcuts</h3>
          </div>
          <ul className="text-sm space-y-2" style={{ color: "var(--mf-text-muted)" }}>
            <li><kbd className="px-2 py-0.5 rounded text-xs" style={{ background: "var(--mf-muted)" }}>N</kbd> New note</li>
            <li><kbd className="px-2 py-0.5 rounded text-xs" style={{ background: "var(--mf-muted)" }}>C</kbd> New client</li>
            <li><kbd className="px-2 py-0.5 rounded text-xs" style={{ background: "var(--mf-muted)" }}>S</kbd> Schedule session</li>
            <li><kbd className="px-2 py-0.5 rounded text-xs" style={{ background: "var(--mf-muted)" }}>/</kbd> Ask Sage</li>
          </ul>
        </Card>

        <Card delay={0.25} testid="settings-languages">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5" style={{ color: "var(--mf-primary)" }} />
            <h3 className="font-serif text-xl">Languages</h3>
          </div>
          <ul className="text-sm space-y-2" style={{ color: "var(--mf-text-muted)" }}>
            <li>• English</li>
            <li>• Spanish</li>
            <li>• Mandarin</li>
            <li>• French</li>
            <li>• Arabic</li>
            <li>• Portuguese</li>
          </ul>
        </Card>

        <Card delay={0.3} testid="settings-account">
          <h3 className="font-serif text-xl mb-4">Account</h3>
          <button onClick={logout} className="mf-btn-primary" style={{ background: "#B45253" }} data-testid="settings-logout">Sign Out</button>
        </Card>
      </div>
    </Page>
  );
}
